import React, { useState, useEffect, useRef } from "react";
import {
  playersAPI,
  weeksAPI,
  gamesAPI,
  picksAPI,
  handleApiError,
} from "../services/api";

function MakePicks() {
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [showAddPlayer, setShowAddPlayer] = useState(false);

  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState({});
  const [existingPicks, setExistingPicks] = useState({});
  const [pickCounts, setPickCounts] = useState({}); // Track how many picked each team
  // Which (player, week) the picks state currently belongs to. Guards the
  // duplicate check from running against a previous player's picks during the
  // async reload that follows a player switch.
  const [picksLoadedFor, setPicksLoadedFor] = useState(null);

  // Everyone's picks for the selected week, shown when no single player is chosen.
  const [weekSummary, setWeekSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Monotonic id so a slow duplicate-check response can't overwrite a newer one.
  const dupReqId = useRef(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [savingPicks, setSavingPicks] = useState({}); // Track which picks are being saved
  const [duplicateCheck, setDuplicateCheck] = useState(null); // Track duplicate pick detection
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  const [viewMode, setViewMode] = useState("current"); // 'current' or 'historical'

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for duplicates when all picks are made
  useEffect(() => {
    const loadedForCurrent =
      picksLoadedFor &&
      selectedPlayer &&
      selectedWeek &&
      picksLoadedFor.playerId === selectedPlayer.id &&
      picksLoadedFor.weekId === selectedWeek.id;

    if (
      viewMode === "current" &&
      loadedForCurrent &&
      allPicksMade() &&
      selectedWeek.is_active
    ) {
      checkForDuplicates();
    } else {
      // Clear duplicate check if conditions are no longer met
      setDuplicateCheck(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, picksLoadedFor, selectedPlayer, selectedWeek, games, viewMode]);

  // Load the selected player's picks when the player or week changes. Clear the
  // old picks synchronously first so nothing downstream reads a stale player's
  // selections.
  useEffect(() => {
    setPicks({});
    setExistingPicks({});
    setPicksLoadedFor(null);
    setDuplicateCheck(null);
    if (selectedPlayer && selectedWeek) {
      loadPlayerPicks(selectedPlayer.id, selectedWeek.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer, selectedWeek]);

  // Load everyone's picks for the summary view when no player is selected.
  useEffect(() => {
    if (selectedPlayer || !selectedWeek) {
      setWeekSummary(null);
      return;
    }
    loadWeekSummary(selectedWeek.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayer, selectedWeek]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadPlayers(), loadWeeks()]);
    } catch (error) {
      setError("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      const response = await playersAPI.getAll();
      setPlayers(response.data);
    } catch (error) {
      setError(handleApiError(error, "Failed to load players"));
    }
  };

  const loadWeeks = async () => {
    try {
      const response = await weeksAPI.getAll();
      const allWeeks = Array.isArray(response.data) ? response.data : [];

      // Only show the current season's weeks (the season of the active week).
      const activeWeek = allWeeks.find((week) => week.is_active);
      const currentSeason = activeWeek
        ? activeWeek.season
        : Math.max(0, ...allWeeks.map((w) => w.season));
      const seasonWeeks = allWeeks
        .filter((w) => w.season === currentSeason)
        .sort((a, b) => a.week_number - b.week_number);
      setWeeks(seasonWeeks);

      if (activeWeek) {
        setSelectedWeek(activeWeek);
        await Promise.all([
          loadGamesForWeek(activeWeek.id),
          loadPickCounts(activeWeek.id),
        ]);
      }
    } catch (error) {
      setError(handleApiError(error, "Failed to load weeks"));
    }
  };

  const loadGamesForWeek = async (weekId) => {
    try {
      // Players only pick from the games the admin selected for the week.
      const response = await gamesAPI.getAll(weekId, { selectedOnly: true });
      const sortedGames = (response.data || []).sort(
        (a, b) => new Date(a.game_time) - new Date(b.game_time)
      );
      setGames(sortedGames);
    } catch (error) {
      setError(handleApiError(error, "Failed to load games"));
    }
  };

  const loadPickCounts = async (weekId) => {
    try {
      const response = await picksAPI.getWeekPickCounts(weekId);
      setPickCounts(response.data || {});
    } catch (error) {
      // Don't show error for pick counts - it's supplementary information
      setPickCounts({});
    }
  };

  const loadPlayerPicks = async (playerId, weekId) => {
    try {
      const response = await picksAPI.getAll({
        player_id: playerId,
        week_id: weekId,
      });
      const playerPicks = {};
      if (response.data && Array.isArray(response.data)) {
        response.data.forEach((pick) => {
          playerPicks[pick.game_id] = pick.picked_team;
        });
      }
      setExistingPicks(playerPicks);
      setPicks(playerPicks);
      setPicksLoadedFor({ playerId, weekId });
    } catch (error) {
      // Don't show error for no picks found
      setExistingPicks({});
      setPicks({});
      setPicksLoadedFor({ playerId, weekId });
    }
  };

  const loadWeekSummary = async (weekId) => {
    try {
      setLoadingSummary(true);
      const response = await picksAPI.getWeekSummary(weekId);
      setWeekSummary(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setWeekSummary([]);
    } finally {
      setLoadingSummary(false);
    }
  };

  const addNewPlayer = async () => {
    if (addingPlayer) return; // Prevent multiple submissions

    if (!newPlayerName.trim()) {
      setError("Player name is required");
      return;
    }

    const trimmedName = newPlayerName.trim();

    // Check if player already exists in our current list
    const existingPlayer =
      players &&
      players.find((p) => p.name.toLowerCase() === trimmedName.toLowerCase());

    if (existingPlayer) {
      setError(`Player "${trimmedName}" already exists`);
      return;
    }

    try {
      setAddingPlayer(true);
      const response = await playersAPI.create({ name: trimmedName });
      const newPlayer = response.data;
      setPlayers([...players, newPlayer]);
      setSelectedPlayer(newPlayer);
      setNewPlayerName("");
      setShowAddPlayer(false);
      setSuccess(`Player "${newPlayer.name}" added successfully`);
      clearMessages(3000);
    } catch (error) {
      if (error.response?.status === 409) {
        setError(`Player "${trimmedName}" already exists`);
      } else {
        setError(handleApiError(error, "Failed to add player"));
      }
    } finally {
      setAddingPlayer(false);
    }
  };

  // Picks/summary loading is handled by the effects keyed on selectedPlayer /
  // selectedWeek, so these just update the selection.
  const selectPlayer = (player) => {
    setSelectedPlayer(player);
  };

  const selectWeek = async (week) => {
    setSelectedWeek(week);
    await Promise.all([loadGamesForWeek(week.id), loadPickCounts(week.id)]);
  };

  const makePick = async (gameId, team) => {
    if (!selectedPlayer || !selectedWeek) {
      setError("Please select a player and week");
      return;
    }

    // Optimistic update - show the pick immediately
    setPicks((prev) => ({
      ...prev,
      [gameId]: team,
    }));

    // Show loading state for this specific pick
    setSavingPicks((prev) => ({
      ...prev,
      [gameId]: true,
    }));

    try {
      // Check if pick already exists
      const existingPickId = existingPicks[gameId]
        ? await findExistingPickId(selectedPlayer.id, gameId)
        : null;

      if (existingPickId) {
        // Update existing pick
        await picksAPI.update(existingPickId, {
          game_id: gameId,
          player_id: selectedPlayer.id,
          picked_team: team,
        });
      } else {
        // Create new pick
        await picksAPI.create({
          game_id: gameId,
          player_id: selectedPlayer.id,
          picked_team: team,
        });
      }

      // Update existing picks to track what's been saved
      setExistingPicks((prev) => ({
        ...prev,
        [gameId]: team,
      }));

      // Refresh pick counts to show updated counts for all players
      if (selectedWeek) {
        loadPickCounts(selectedWeek.id);
      }

      // Clear any previous errors
      setError("");
    } catch (error) {
      // Revert the optimistic update on error
      setPicks((prev) => ({
        ...prev,
        [gameId]: existingPicks[gameId] || undefined,
      }));

      setError(handleApiError(error, "Failed to save pick"));
    } finally {
      // Remove loading state
      setSavingPicks((prev) => {
        const newState = { ...prev };
        delete newState[gameId];
        return newState;
      });
    }
  };

  // Helper function to find existing pick ID
  const findExistingPickId = async (playerId, gameId) => {
    try {
      const response = await picksAPI.getAll({
        player_id: playerId,
        game_id: gameId,
      });
      return response.data && response.data.length > 0
        ? response.data[0].id
        : null;
    } catch (error) {
      return null;
    }
  };

  const canEditPicks = () => {
    if (!games || !games.length) return false;

    // Find the earliest game time for the week
    const earliestGameTime = Math.min(
      ...games.map((game) => new Date(game.game_time).getTime())
    );
    const now = new Date().getTime();

    // Can edit if earliest game hasn't started yet
    return now < earliestGameTime;
  };

  const allPicksMade = () => {
    return games && games.length > 0 && games.every((game) => picks[game.id]);
  };

  const checkForDuplicates = async () => {
    if (!selectedPlayer || !selectedWeek || !allPicksMade()) {
      return;
    }

    const reqId = ++dupReqId.current;
    try {
      setCheckingDuplicates(true);
      // Pass current picks state to the API for real-time checking
      const response = await picksAPI.checkDuplicates(
        selectedPlayer.id,
        selectedWeek.id,
        picks
      );
      if (reqId !== dupReqId.current) return; // superseded by a newer check
      setDuplicateCheck(response.data);
    } catch (error) {
      if (reqId === dupReqId.current) {
        console.error("Error checking for duplicates:", error);
        // Don't show error for duplicate check failures - it's not critical
      }
    } finally {
      if (reqId === dupReqId.current) setCheckingDuplicates(false);
    }
  };

  const formatGameTime = (gameTime) => {
    return new Date(gameTime).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const clearMessages = (timeout = 0) => {
    if (timeout > 0) {
      setTimeout(() => {
        setError("");
        setSuccess("");
      }, timeout);
    } else {
      setError("");
      setSuccess("");
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="make-picks">
      <div className="picks-header">
        <h2>Make Your Picks</h2>

        {/* View Mode Toggle */}
        <div className="view-mode-toggle">
          <button
            onClick={() => setViewMode("current")}
            className={`toggle-btn ${viewMode === "current" ? "active" : ""}`}
          >
            Current Week
          </button>
          <button
            onClick={() => setViewMode("historical")}
            className={`toggle-btn ${
              viewMode === "historical" ? "active" : ""
            }`}
          >
            Historical Picks
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError("")} className="alert-close">
            &times;
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess("")} className="alert-close">
            &times;
          </button>
        </div>
      )}

      {/* Player Selection */}
      <div className="player-selection">
        <h3>Select Player</h3>
        <div className="player-controls">
          <select
            value={selectedPlayer?.id || ""}
            onChange={(e) => {
              if (!e.target.value) {
                setSelectedPlayer(null);
                return;
              }
              const player = players.find(
                (p) => p.id === parseInt(e.target.value)
              );
              if (player) selectPlayer(player);
            }}
            className="player-dropdown"
          >
            <option value="">Choose a player...</option>
            {players &&
              players.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.name}
                </option>
              ))}
          </select>

          <button
            onClick={() => setShowAddPlayer(!showAddPlayer)}
            className="btn-secondary"
          >
            {showAddPlayer ? "Cancel" : "Add New Player"}
          </button>
        </div>

        {showAddPlayer && (
          <div className="add-player-form">
            <input
              type="text"
              placeholder="Enter player name"
              value={newPlayerName}
              onChange={(e) => setNewPlayerName(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && addNewPlayer()}
              className={`player-name-input ${
                newPlayerName.trim() &&
                players &&
                players.find(
                  (p) =>
                    p.name.toLowerCase() === newPlayerName.trim().toLowerCase()
                )
                  ? "duplicate-name"
                  : ""
              }`}
            />
            <button
              onClick={addNewPlayer}
              disabled={addingPlayer}
              className="btn-primary"
            >
              {addingPlayer ? "Adding..." : "Add Player"}
            </button>
            {newPlayerName.trim() &&
              players &&
              players.find(
                (p) =>
                  p.name.toLowerCase() === newPlayerName.trim().toLowerCase()
              ) && (
                <span className="duplicate-warning">Player already exists</span>
              )}
          </div>
        )}
      </div>

      {/* Week Selection */}
      {weeks && weeks.length > 0 && (
        <div className="week-selection">
          <h3>Select Week</h3>
          <div className="week-tabs">
            {weeks.map((week) => (
              <button
                key={week.id}
                onClick={() => selectWeek(week)}
                className={`week-tab ${
                  selectedWeek?.id === week.id ? "active" : ""
                }`}
              >
                Week {week.week_number}
              </button>
            ))}
          </div>
        </div>
      )}

      {!selectedPlayer && selectedWeek && games && games.length > 0 && (
        <p className="summary-hint">
          Viewing everyone's picks. Choose your name above to make or edit your
          own picks.
        </p>
      )}

      {selectedWeek && (
        <div className="picks-content">
          {!games || games.length === 0 ? (
            <div className="no-games">
              <h3>No Games Available</h3>
              <p>
                Picks will be coming soon! The admin hasn't added games for Week{" "}
                {selectedWeek.week_number} yet.
              </p>
            </div>
          ) : !selectedPlayer ? (
            <div className="picks-summary">
              <h3>Week {selectedWeek.week_number} - Everyone's Picks</h3>
              {loadingSummary ? (
                <div className="loading">Loading picks…</div>
              ) : !weekSummary ||
                weekSummary.every((p) => p.pick_count === 0) ? (
                <div className="no-summary">
                  <p>
                    No one has made their picks for Week{" "}
                    {selectedWeek.week_number} yet.
                  </p>
                </div>
              ) : (
                <div className="summary-scroll">
                  <table className="summary-table">
                    <thead>
                      <tr>
                        <th className="summary-player-col">Player</th>
                        {games.map((g) => (
                          <th key={g.id}>
                            <span className="summary-away">{g.away_team}</span>
                            <span className="summary-at">@</span>
                            <span className="summary-home">{g.home_team}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {weekSummary.map((p) => (
                        <tr key={p.player_id}>
                          <td className="summary-player-col">
                            {p.name}
                            {p.pick_count === 0 && (
                              <span className="muted-tag">
                                hasn't picked yet
                              </span>
                            )}
                          </td>
                          {games.map((g) => {
                            const cell = p.picks[g.id];
                            if (!cell) {
                              return (
                                <td key={g.id} className="summary-empty">
                                  —
                                </td>
                              );
                            }
                            const cls =
                              cell.is_correct === true
                                ? "correct"
                                : cell.is_correct === false
                                ? "incorrect"
                                : "";
                            return (
                              <td key={g.id} className={`summary-pick ${cls}`}>
                                {cell.picked_team}
                                {cell.is_correct === true && " ✓"}
                                {cell.is_correct === false && " ✗"}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <>
                  <div className="picks-info">
                    <h3>
                      {selectedPlayer.name}'s Picks - Week{" "}
                      {selectedWeek.week_number}
                      {selectedWeek.is_active && (
                        <span className="current-week"> (Current Week)</span>
                      )}
                    </h3>

                    {viewMode === "current" && selectedWeek.is_active && (
                      <div className="picks-status">
                        {canEditPicks() ? (
                          <span className="edit-allowed">
                            ✓ Picks can be edited until games start
                          </span>
                        ) : (
                          <span className="edit-locked">
                            🔒 Picks locked - games have started
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="games-list">
                    {games.map((game) => {
                      const weekEditable =
                        viewMode === "current" &&
                        selectedWeek.is_active &&
                        canEditPicks() &&
                        !game.is_complete;
                      const currentPick = picks[game.id];
                      const counts = pickCounts[game.id];
                      const hasSpread =
                        game.spread !== null && game.spread !== undefined;
                      const sides = [
                        {
                          team: game.away_team,
                          spread: hasSpread ? -game.spread : null,
                          count: counts ? counts.away_team_picks || 0 : null,
                        },
                        {
                          team: game.home_team,
                          spread: hasSpread ? game.spread : null,
                          count: counts ? counts.home_team_picks || 0 : null,
                        },
                      ];

                      return (
                        <div
                          key={game.id}
                          className={`game-card ${
                            game.is_complete ? "completed" : "pending"
                          }`}
                        >
                          <div className="game-time">
                            {formatGameTime(game.game_time)}
                          </div>

                          <div className="team-tiles">
                            {sides.map((side, i) => {
                              const picked = currentPick === side.team;
                              const isWinner =
                                game.is_complete && game.winner === side.team;
                              const missed =
                                game.is_complete &&
                                picked &&
                                game.winner &&
                                game.winner !== side.team;
                              return (
                                <React.Fragment key={side.team}>
                                  {i === 1 && (
                                    <span className="tile-at">@</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      weekEditable &&
                                      makePick(game.id, side.team)
                                    }
                                    disabled={
                                      !weekEditable || !!savingPicks[game.id]
                                    }
                                    className={`team-tile ${
                                      picked ? "picked" : ""
                                    } ${isWinner ? "winner" : ""} ${
                                      missed ? "missed" : ""
                                    } ${weekEditable ? "editable" : "locked"}`}
                                  >
                                    <span className="tile-team">
                                      {side.team}
                                    </span>
                                    {side.spread !== null && (
                                      <span className="tile-spread">
                                        {side.spread > 0 ? "+" : ""}
                                        {side.spread.toFixed(1)}
                                      </span>
                                    )}
                                    <span className="tile-meta">
                                      {savingPicks[game.id] && picked && (
                                        <span className="saving-spinner">
                                          ⟳
                                        </span>
                                      )}
                                      {side.count !== null && (
                                        <span className="pick-count">
                                          {side.count} pick
                                          {side.count === 1 ? "" : "s"}
                                        </span>
                                      )}
                                      {picked && (
                                        <span className="tile-check">✓</span>
                                      )}
                                    </span>
                                  </button>
                                </React.Fragment>
                              );
                            })}
                          </div>

                          {weekEditable ? (
                            <div className="tile-footer">
                              {!currentPick ? (
                                <span className="tile-hint">
                                  Tap a team to make your pick
                                </span>
                              ) : existingPicks[game.id] &&
                                !savingPicks[game.id] ? (
                                <span className="saved-indicator">
                                  ✓ Saved
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="tile-footer">
                              <span
                                className={`pick-readout ${
                                  game.is_complete && existingPicks[game.id]
                                    ? existingPicks[game.id] === game.winner
                                      ? "correct"
                                      : "incorrect"
                                    : ""
                                }`}
                              >
                                Pick:{" "}
                                {existingPicks[game.id] || "No pick made"}
                                {game.is_complete &&
                                  existingPicks[game.id] &&
                                  (existingPicks[game.id] === game.winner
                                    ? " ✓"
                                    : " ✗")}
                              </span>
                              {game.is_complete && game.winner && (
                                <span className="game-winner">
                                  Winner: {game.winner}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Duplicate Picks Warning Banner */}
                  {duplicateCheck &&
                    duplicateCheck.has_duplicates &&
                    viewMode === "current" && (
                      <div className="duplicate-warning-banner">
                        <div className="duplicate-alert">
                          <div className="duplicate-header">
                            <span className="warning-icon">⚠️</span>
                            <span className="warning-text">
                              Your picks match{" "}
                              {duplicateCheck.duplicate_players
                                .map((p) => p.player_name)
                                .join(" and ")}
                              !
                            </span>
                          </div>
                          <div className="duplicate-message">
                            {duplicateCheck.suggestions &&
                            duplicateCheck.suggestions.length > 0
                              ? "Here are changes that would make your picks unique:"
                              : "No single pick change will make your selections unique. Consider changing multiple picks."}
                          </div>
                          {duplicateCheck.suggestions &&
                            duplicateCheck.suggestions.length > 0 && (
                              <div className="suggestions-list">
                                {duplicateCheck.suggestions.map(
                                  (suggestion, index) => (
                                    <div
                                      key={suggestion.game_id}
                                      className="suggestion-item"
                                    >
                                      <span className="suggestion-game">
                                        {suggestion.away_team} @{" "}
                                        {suggestion.home_team}:
                                      </span>
                                      <span className="suggestion-change">
                                        Switch from{" "}
                                        <strong>
                                          {suggestion.current_team}
                                        </strong>{" "}
                                        ({suggestion.current_count} picks) to{" "}
                                        <strong>
                                          {suggestion.suggested_team}
                                        </strong>{" "}
                                        ({suggestion.suggested_count} picks)
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                  {checkingDuplicates && viewMode === "current" && (
                    <div className="checking-duplicates">
                      <span className="spinner">🔄</span> Checking for
                      duplicates...
                    </div>
                  )}

                  {viewMode === "current" && selectedWeek.is_active && (
                    <div className="picks-progress">
                      <span className="progress-info">
                        Progress: {Object.keys(existingPicks).length}/
                        {games.length} picks saved
                        {allPicksMade() && (
                          <span className="all-complete">
                            {" "}
                            🎉 All picks complete!
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
    </div>
  );
}

export default MakePicks;
